import ENV from '../../env.json';

/**
 * Safe impersonation (design spec §7, plan artifacts/plan_r1_safe_impersonation.md):
 * the employee-app pages also run inside the admin origin under `/impersonate`,
 * signed in as the ADMIN. In that mode every backend call:
 *  - goes straight to the Cloud Functions host (the admin hosting target has
 *    no `/api/*` rewrites for the employee-app endpoints), and
 *  - carries `Authorization: Bearer <admin Firebase ID token>`, which switches
 *    the backend to its permission-checked impersonator path.
 * The employee mode (real employees, token links, optional SSO) is unchanged.
 */

/** What the session was started with (`/impersonate/start?...`). */
export type ImpersonationStartParams = {
  orgId: string;
  userId: string;
  instanceId?: string | null;
  actorKey?: string | null;
};

type ImpersonationConfig = {
  getIdToken: () => Promise<string>;
  /** Where "Exit" goes (the admin app). */
  exitUrl: string;
  start: ImpersonationStartParams;
};

let impersonation: ImpersonationConfig | null = null;

export const enableImpersonationMode = (cfg: ImpersonationConfig) => {
  impersonation = cfg;
};

export const isImpersonationMode = () => impersonation !== null;

export const getImpersonationConfig = () => impersonation;

/**
 * A link other admins can open: the permission-checked entry URL, never the
 * current URL (its handles are sealed for THIS admin only). Whoever opens it signs
 * in as themselves and sees only what their own permissions allow.
 */
export const toImpersonationShareUrl = (
  start: ImpersonationStartParams,
  origin = window.location.origin
) => {
  const u = new URL('/impersonate/start', origin);
  u.searchParams.set('orgId', start.orgId);
  u.searchParams.set('userId', start.userId);
  if (start.instanceId) u.searchParams.set('instanceId', start.instanceId);
  if (start.actorKey) u.searchParams.set('actorKey', start.actorKey);
  return u.toString();
};

const isLocalhost = () => window.location.host.startsWith('localhost');

/**
 * URL of an employee-app endpoint. `fnName` is the deployed function name
 * (used on localhost and in impersonation mode); `hostingPath` is the
 * standalone-app hosting rewrite (used by real employees in production).
 */
export const standaloneFunctionUrl = (fnName: string, hostingPath: string) => {
  if (impersonation || isLocalhost()) {
    const u = new URL(ENV.domains.cf.public);
    u.pathname = fnName;
    return u;
  }
  const u = new URL(window.location.origin);
  u.pathname = hostingPath;
  return u;
};

/** `fetch`, plus the admin's bearer token in impersonation mode. */
export const standaloneFetch = async (
  input: string,
  init: RequestInit = {}
): Promise<Response> => {
  if (!impersonation) return fetch(input, init);
  const idToken = await impersonation.getIdToken();
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${idToken}`);
  return fetch(input, { ...init, headers });
};
